import { useEffect, useState, useCallback } from 'react';
import RegistrationPaymentsPanel from './RegistrationPaymentsPanel';
import { toast } from 'sonner';
import { axiosInstance, getApiErrorMessage, inputCls, Modal } from './_shared';

export default function AdminRegistrationView({ event, onBack }) {
  const [section, setSection] = useState('registrations');   // registrations | payments
  const [regs, setRegs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkStatsData, setBulkStatsData] = useState('');
  const [uploadingStats, setUploadingStats] = useState(false);
  const [showTeamOwnerApproval, setShowTeamOwnerApproval] = useState(null); // registration object
  const [gstRateOverride, setGstRateOverride] = useState('18');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/v1/admin/events/${event.id}/registrations`;
      const params = [];
      if (filterRole) params.push(`role=${filterRole}`);
      if (filterStatus) params.push(`status=${filterStatus}`);
      if (params.length) url += '?' + params.join('&');
      const [regRes, statsRes] = await Promise.all([
        axiosInstance.get(url),
        axiosInstance.get(`/v1/admin/events/${event.id}/registration-stats`),
      ]);
      setRegs(regRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Failed to load registrations'); }
    finally { setLoading(false); }
  }, [event.id, filterRole, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const review = async (regId, decision) => {
    try {
      await axiosInstance.patch(`/v1/admin/registrations/${regId}/review`, { decision });
      toast.success(`Registration ${decision}`);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  // Approve Team Owner with GST
  const approveTeamOwner = async () => {
    if (!showTeamOwnerApproval) return;
    setApproving(true);
    try {
      const gstRate = parseFloat(gstRateOverride) / 100;
      await axiosInstance.post(`/v1/admin/registrations/${showTeamOwnerApproval.id}/approve-team-owner`, {
        gst_rate_override: gstRate,
        review_notes: approvalNotes,
      });
      toast.success('Team owner approved! Payment request sent.');
      setShowTeamOwnerApproval(null);
      setGstRateOverride('18');
      setApprovalNotes('');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to approve'));
    } finally {
      setApproving(false);
    }
  };

  // Download stats template CSV
  const downloadStatsTemplate = async () => {
    try {
      const res = await axiosInstance.get(`/v1/admin/events/${event.id}/registrations/stats-template`);
      const players = res.data.players;
      if (!players.length) {
        toast.error('No players registered yet');
        return;
      }
      const headers = [
        'registration_id', 'user_name', 'user_email', 'playing_role', 'cricheroes_url',
        // Batting
        'matches', 'batting_innings', 'runs', 'highest_score', 'batting_avg', 'strike_rate',
        'not_outs', 'fours', 'sixes', 'fifties', 'thirties',
        // Bowling
        'bowling_innings', 'overs', 'wickets', 'best_bowling', 'economy',
        'bowling_avg', 'maidens',
        // Fielding
        'catches', 'stumpings', 'run_outs',
      ];
      const rows = players.map(p => {
        const s = p.existing_stats || {};
        return [
          p.registration_id, p.user_name, p.user_email, p.playing_role, p.cricheroes_url || '',
          s.matches ?? '', s.batting_innings ?? s.innings ?? '', s.runs ?? '', s.highest_score ?? '',
          s.batting_avg ?? s.average ?? '', s.strike_rate ?? '', s.not_outs ?? '',
          s.fours ?? '', s.sixes ?? '', s.fifties ?? '', s.thirties ?? '',
          s.bowling_innings ?? '', s.overs ?? '', s.wickets ?? '', s.best_bowling ?? '',
          s.economy ?? '', s.bowling_avg ?? '', s.maidens ?? '',
          s.catches ?? '', s.stumpings ?? '', s.run_outs ?? '',
        ];
      });
      const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `player_stats_template_${event.id.slice(0,8)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to download template'));
    }
  };

  // Upload bulk stats from CSV
  const handleBulkStatsUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingStats(true);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      
      const parseIntOrNull = (v) => { const n = parseInt(v); return isNaN(n) ? null : n; };
      const parseFloatOrNull = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };

      // Parse rows
      const items = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].match(/(".*?"|[^",]+)/g)?.map(v => v.replace(/^"|"$/g, '').trim()) || [];
        if (!values.length) continue;
        
        const row = {};
        headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
        
        if (!row.registration_id) continue;
        
        // Build statistics dict — only include non-null values
        const stats = {};
        const intFields = [
          'matches', 'batting_innings', 'runs', 'highest_score', 'not_outs',
          'fours', 'sixes', 'fifties', 'thirties',
          'bowling_innings', 'wickets', 'maidens',
          'catches', 'stumpings', 'run_outs',
        ];
        const floatFields = [
          'batting_avg', 'strike_rate', 'economy', 'bowling_avg', 'overs',
        ];
        const stringFields = ['best_bowling'];

        for (const f of intFields) {
          if (row[f]) stats[f] = parseIntOrNull(row[f]);
        }
        for (const f of floatFields) {
          if (row[f]) stats[f] = parseFloatOrNull(row[f]);
        }
        for (const f of stringFields) {
          if (row[f]) stats[f] = row[f];
        }

        // Backward compat: map old column names if present
        if (row.average && !stats.batting_avg) stats.batting_avg = parseFloatOrNull(row.average);
        if (row.innings && !stats.batting_innings) stats.batting_innings = parseIntOrNull(row.innings);

        items.push({
          registration_id: row.registration_id,
          statistics: stats,
        });
      }

      if (!items.length) {
        toast.error('No valid data found in CSV');
        setUploadingStats(false);
        return;
      }

      const res = await axiosInstance.post(`/v1/admin/events/${event.id}/registrations/bulk-stats`, items);
      toast.success(`Updated ${res.data.updated} player stats`);
      if (res.data.errors?.length) {
        toast.warning(`${res.data.errors.length} errors occurred`);
      }
      load();
      setShowBulkUpload(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to upload stats'));
    } finally {
      setUploadingStats(false);
    }
  };

  const roleBadge = (role) => {
    const map = { player: 'bg-blue-100 text-blue-700', team_owner: 'bg-purple-100 text-purple-700', sponsor: 'bg-orange-100 text-orange-700' };
    return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${map[role] || 'bg-gray-100'}`}>{role?.replace('_', ' ')}</span>;
  };
  const statusBadgeReg = (s) => {
    const map = { 
      pending: 'bg-yellow-100 text-yellow-700', 
      approved: 'bg-green-100 text-green-700', 
      approved_pending_payment: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-600', 
      withdrawn: 'bg-gray-100 text-gray-500', 
      draft: 'bg-gray-100 text-gray-500' 
    };
    const labels = {
      approved_pending_payment: 'awaiting payment',
    };
    return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${map[s] || 'bg-gray-100'}`}>{labels[s] || s}</span>;
  };

  return (
    <div data-testid="admin-registrations-view">
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4">
        <i className="ph-bold ph-arrow-left" /> Back to Events
      </button>
      <h2 className="text-lg font-extrabold mb-1">{event.title}</h2>
      <p className="text-xs text-gray-400 mb-4">Tournament Registrations</p>

      <div className="flex gap-2 mb-4" data-testid="registrations-sections">
        {[['registrations', 'Registrations', 'ph-users'], ['payments', 'Payments & refunds', 'ph-currency-inr']].map(([key, label, icon]) => (
          <button key={key} onClick={() => setSection(key)}
            className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 ${section === key ? 'bg-[#2B3A67] text-white' : 'bg-white border border-gray-200 text-[#2B3A67]'}`}
            data-testid={`section-${key}`}>
            <i className={`ph-bold ${icon}`} /> {label}
          </button>
        ))}
      </div>

      {section === 'payments' ? <RegistrationPaymentsPanel event={event} /> : (<>

      {stats && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <p className="text-2xl font-extrabold">{stats.total}</p><p className="text-xs text-gray-400">Total</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-extrabold text-blue-700">{stats.by_role?.player || 0}</p><p className="text-xs text-blue-600">Players</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-extrabold text-purple-700">{stats.by_role?.team_owner || 0}</p><p className="text-xs text-purple-600">Team Owners</p>
          </div>
          <div className="bg-orange-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-extrabold text-orange-700">{stats.by_role?.sponsor || 0}</p><p className="text-xs text-orange-600">Sponsors</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <select className={inputCls + ' !w-auto'} value={filterRole} onChange={e => setFilterRole(e.target.value)} data-testid="filter-role">
          <option value="">All Roles</option>
          <option value="player">Player</option>
          <option value="team_owner">Team Owner</option>
          <option value="sponsor">Sponsor</option>
        </select>
        <select className={inputCls + ' !w-auto'} value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="filter-status">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved_pending_payment">Awaiting Payment</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="draft">Draft</option>
        </select>
        <div className="ml-auto flex gap-2">
          <button 
            onClick={downloadStatsTemplate}
            className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold flex items-center gap-1.5 hover:bg-blue-100"
            data-testid="download-stats-template-btn"
          >
            <i className="ph-bold ph-download-simple"></i> Stats Template
          </button>
          <button 
            onClick={() => setShowBulkUpload(true)}
            className="px-3 py-2 rounded-lg bg-green-50 text-green-700 text-xs font-bold flex items-center gap-1.5 hover:bg-green-100"
            data-testid="bulk-upload-stats-btn"
          >
            <i className="ph-bold ph-upload-simple"></i> Upload Stats
          </button>
        </div>
      </div>

      {/* Bulk Stats Upload Modal */}
      {showBulkUpload && (
        <Modal title="Bulk Upload Player Statistics" onClose={() => setShowBulkUpload(false)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Upload a CSV file with player statistics. Download the template first to get the correct format.
            </p>
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-xs text-blue-700 font-medium mb-2">CSV Format:</p>
              <p className="text-xs text-blue-600">registration_id, matches, runs, wickets, average, strike_rate, economy</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Select CSV File</label>
              <input 
                type="file" 
                accept=".csv"
                onChange={handleBulkStatsUpload}
                disabled={uploadingStats}
                className="text-sm text-gray-500 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-[#2B3A67] file:text-white"
                data-testid="bulk-stats-file-input"
              />
            </div>
            {uploadingStats && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#2B3A67]"></div>
                Uploading...
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Team Owner Approval Modal */}
      {showTeamOwnerApproval && (
        <Modal title="Approve Team Owner Registration" onClose={() => setShowTeamOwnerApproval(null)}>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="font-bold text-sm">{showTeamOwnerApproval.registration_data?.owner_name}</p>
              <p className="text-xs text-gray-500">{showTeamOwnerApproval.registration_data?.team_name}</p>
              <p className="text-xs text-gray-400 mt-1">
                {showTeamOwnerApproval.registration_data?.ownership_type} ownership
                {showTeamOwnerApproval.registration_data?.gst_number && (
                  <span className="ml-2">| GST: {showTeamOwnerApproval.registration_data?.gst_number}</span>
                )}
              </p>
            </div>

            {showTeamOwnerApproval.registration_data?.ownership_type === 'Corporate' && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">GST Rate (%)</label>
                <input 
                  type="number"
                  value={gstRateOverride}
                  onChange={e => setGstRateOverride(e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  className={inputCls}
                  data-testid="gst-rate-input"
                />
                <p className="text-xs text-gray-400 mt-1">Default is 18%. Modify only in exceptional cases.</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Review Notes (Optional)</label>
              <textarea 
                value={approvalNotes}
                onChange={e => setApprovalNotes(e.target.value)}
                rows={2}
                className={inputCls + ' resize-none'}
                placeholder="Any notes for this approval..."
                data-testid="approval-notes-input"
              />
            </div>

            {/* Fee Preview */}
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-xs font-bold text-green-800 mb-2">PAYMENT WILL BE REQUESTED</p>
              {(() => {
                const baseFee = showTeamOwnerApproval.registration_data?.calculated_total_fee || showTeamOwnerApproval.payment_amount || 0;
                const gstRate = showTeamOwnerApproval.registration_data?.ownership_type === 'Corporate' ? parseFloat(gstRateOverride) / 100 : 0;
                const baseAmount = Math.round(baseFee / (1 + (showTeamOwnerApproval.registration_data?.calculated_gst_rate || 0)));
                const gstAmount = Math.round(baseAmount * gstRate);
                const totalAmount = baseAmount + gstAmount;
                return (
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Base Fee</span>
                      <span>₹{(baseAmount / 100).toLocaleString()}</span>
                    </div>
                    {gstRate > 0 && (
                      <div className="flex justify-between text-amber-700">
                        <span>GST ({(gstRate * 100).toFixed(1)}%)</span>
                        <span>+ ₹{(gstAmount / 100).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold border-t border-green-200 pt-1 mt-1">
                      <span>Total</span>
                      <span>₹{(totalAmount / 100).toLocaleString()}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="flex gap-2">
              <button 
                onClick={approveTeamOwner}
                disabled={approving}
                className="flex-1 py-2.5 bg-green-600 text-white font-bold rounded-xl text-sm disabled:opacity-50"
                data-testid="confirm-approve-btn"
              >
                {approving ? 'Approving...' : 'Approve & Send Payment Request'}
              </button>
              <button 
                onClick={() => setShowTeamOwnerApproval(null)}
                className="px-4 py-2.5 bg-gray-100 text-gray-700 font-bold rounded-xl text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E54562]" /></div>
      ) : regs.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><p className="text-sm">No registrations found</p></div>
      ) : (
        <div className="space-y-2">
          {regs.map(r => (
            <div key={r.id} className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm" data-testid={`reg-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-bold text-sm">{r.user_name || r.user_email}</p>
                  <p className="text-xs text-gray-400">{r.user_email}</p>
                  <div className="flex gap-1.5 mt-1">{roleBadge(r.role)} {statusBadgeReg(r.status)}
                    {r.payment_status === 'paid' && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-600">₹{(r.payment_amount / 100).toLocaleString()} paid</span>}
                    {r.payment_status === 'partially_refunded' && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">₹{(r.payment_amount / 100).toLocaleString()} paid · partly refunded</span>}
                    {r.payment_status === 'refunded' && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">₹{(r.payment_amount / 100).toLocaleString()} refunded</span>}
                  </div>
                  {r.registration_data && (
                    <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                      {r.role === 'player' && (r.registration_data.playing_role || r.registration_data.primary_role) && (
                        <p>Role: {[r.registration_data.playing_role || r.registration_data.primary_role,
                          r.registration_data.batting_style && `bats ${r.registration_data.batting_style}`,
                          r.registration_data.bowling_style && `bowls ${r.registration_data.bowling_style}`,
                          r.registration_data.bowling_type].filter(Boolean).join(' | ')}</p>
                      )}
                      {r.role === 'player' && (
                        <PlayerCategory reg={r} canEdit={event.can_manage !== false}
                          onChanged={(updated) => setRegs((list) => list.map((x) => (x.id === updated.id ? updated : x)))} />
                      )}
                      {(r.registration_data.profile_snapshot?.mobile_number || r.registration_data.profile_snapshot?.instagram_handle) && (
                        <p className="flex gap-3 flex-wrap">
                          {r.registration_data.profile_snapshot.mobile_number && (
                            <span><i className="ph-bold ph-phone mr-1" />{r.registration_data.profile_snapshot.mobile_number}</span>
                          )}
                          {r.registration_data.profile_snapshot.instagram_handle && (
                            <a href={`https://www.instagram.com/${r.registration_data.profile_snapshot.instagram_handle}/`}
                              target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">
                              <i className="ph-bold ph-instagram-logo mr-1" />@{r.registration_data.profile_snapshot.instagram_handle}
                            </a>
                          )}
                        </p>
                      )}
                      {r.role === 'player' && r.registration_data.available_all_days === 'No' && (
                        <p className="text-amber-700">
                          <i className="ph-bold ph-calendar-x mr-1" />Unavailable: {Array.isArray(r.registration_data.unavailable_dates)
                            ? r.registration_data.unavailable_dates.map((d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })).join(', ')
                            : (r.registration_data.unavailable_dates || '—')}
                        </p>
                      )}
                      {r.role === 'player' && r.registration_data.id_proof_url && (
                        <p><a href={r.registration_data.id_proof_url} target="_blank" rel="noopener noreferrer" className="underline text-blue-600">
                          <i className="ph-bold ph-identification-card mr-1" />ID proof</a></p>
                      )}
                      {r.role === 'player' && r.registration_data.cricheroes_url && (
                        <p className="text-blue-600">
                          <a href={r.registration_data.cricheroes_url} target="_blank" rel="noopener noreferrer" className="underline">
                            <i className="ph-bold ph-link mr-1"></i>CricHeroes Profile
                          </a>
                        </p>
                      )}
                      {r.role === 'player' && r.registration_data.statistics && (
                        <p className="text-green-600">
                          <i className="ph-bold ph-chart-bar mr-1"></i>
                          Stats: {r.registration_data.statistics.matches || 0} M | {r.registration_data.statistics.runs || 0} R | {r.registration_data.statistics.wickets || 0} W
                        </p>
                      )}
                      {r.role === 'team_owner' && (
                        <div className="space-y-1">
                          <p><strong>Owner:</strong> {r.registration_data.owner_name || '—'}</p>
                          <p><strong>Team:</strong> {r.registration_data.team_name || '—'}</p>
                          <p><strong>Phone:</strong> {r.registration_data.owner_phone || '—'} | <strong>Email:</strong> {r.registration_data.owner_email || '—'}</p>
                          <p><strong>Ownership:</strong> {r.registration_data.ownership_type || '—'} 
                            {r.registration_data.ownership_type === 'Corporate' && r.registration_data.gst_number && (
                              <span className="ml-2 text-purple-600">GST: {r.registration_data.gst_number}</span>
                            )}
                          </p>
                          {r.registration_data.final_total_fee && (
                            <p className="text-green-700 font-medium">
                              Fee: ₹{(r.registration_data.final_base_fee / 100).toLocaleString()} 
                              {r.registration_data.final_gst_amount > 0 && (
                                <> + GST ₹{(r.registration_data.final_gst_amount / 100).toLocaleString()}</>
                              )}
                              {' = '}₹{(r.registration_data.final_total_fee / 100).toLocaleString()}
                            </p>
                          )}
                          {r.registration_data.auction_participation && (
                            <p><strong>Auction:</strong> {r.registration_data.auction_participation}</p>
                          )}
                        </div>
                      )}
                      {r.role === 'sponsor' && r.registration_data.company_name && <p><strong>{r.registration_data.company_name}</strong></p>}
                      {r.role === 'sponsor' && r.registration_data.sponsorship_type && <p>Type: {r.registration_data.sponsorship_type} | Budget: {r.registration_data.budget_range}</p>}
                    </div>
                  )}
                </div>
                {r.status === 'pending' && (
                  <div className="flex gap-1 shrink-0">
                    {r.role === 'team_owner' ? (
                      <>
                        <button 
                          onClick={() => {
                            setShowTeamOwnerApproval(r);
                            setGstRateOverride(r.registration_data?.ownership_type === 'Corporate' ? '18' : '0');
                          }} 
                          className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-bold" 
                          data-testid={`approve-reg-${r.id}`}
                        >
                          Approve & Request Payment
                        </button>
                        <button onClick={() => review(r.id, 'rejected')} className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold" data-testid={`reject-reg-${r.id}`}>Reject</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => review(r.id, 'approved')} className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-bold" data-testid={`approve-reg-${r.id}`}>Approve</button>
                        <button onClick={() => review(r.id, 'rejected')} className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold" data-testid={`reject-reg-${r.id}`}>Reject</button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}


// Player category (A–D): the player's own pick, which the event's admins can correct
function PlayerCategory({ reg, canEdit, onChanged }) {
  const [saving, setSaving] = useState(false);
  const data = reg.registration_data || {};
  const change = async (category) => {
    if (category === data.category) return;
    setSaving(true);
    try {
      const res = await axiosInstance.patch(`/v1/admin/registrations/${reg.id}/category`, { category });
      onChanged(res.data);
      toast.success(`Category set to ${category}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not change the category'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex items-center gap-1.5" data-testid={`category-${reg.id}`}>
      <span>Category:</span>
      {canEdit ? ['A', 'B', 'C', 'D'].map((c) => (
        <button key={c} type="button" disabled={saving} onClick={() => change(c)}
          className={`w-6 h-6 rounded-md text-[11px] font-extrabold border ${
            data.category === c ? 'bg-[#2B3A67] text-white border-[#2B3A67]' : 'border-gray-200 text-gray-500'}`}>
          {c}
        </button>
      )) : <strong>{data.category || '—'}</strong>}
      {data.category_self_assessed && data.category_self_assessed !== data.category && (
        <span className="text-gray-400">(player chose {data.category_self_assessed})</span>
      )}
    </div>
  );
}
